import React from "react";
import CustomModal from "components/custom-modal/CustomModal";
import { XIconV1 } from "images/image-component";
import { useTopUp } from "./useTopUp";
import AmountForm from "./AmountForm";
import AccountPicker from "./AccountPicker";
import SigningReview from "./SigningReview";
import type { Network } from "@gardio/contracts/polygon";
import "./topup-modal.css";

export interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Formatted card number shown in the review step and success message. */
  cardNumber: string;
  /** On-chain alias address — the VIP recipient. */
  cardAliasAddress: string;
  network: Network;
}

function TopUpModal({
  isOpen,
  onClose,
  cardNumber,
  cardAliasAddress,
  network,
}: TopUpModalProps) {
  const {
    step,
    phase,
    amount,
    setAmount,
    setPhase,
    selectedAccount,
    treasuryAccount,
    goToStep2,
    goToStep3,
    selectAccount,
    goBack,
    reset,
  } = useTopUp();

  // Block close while on step 3 signing — Step3Review signals busy via its own hook;
  // we prevent close only when the modal is in step 3 but haven't completed yet.
  // Step3Review resets itself before calling onSuccess, so closing after success is safe.
  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSuccess = () => {
    reset();
    onClose();
  };

  return (
    <CustomModal isOpen={isOpen} handleClose={handleClose}>
      <button
        className="v1-modal-close"
        onClick={handleClose}
        aria-label="Close"
      >
        <XIconV1 />
      </button>

      {/* Step indicator */}
      <div className="tu-stepper" aria-label="Progress">
        {/* {([1, 2, 3] as const).map((s) => ( */}
        {([1, 2, 3] as const).map((ph) => (
          <React.Fragment key={ph}>
            <div
              className={[
                "tu-step-dot",
                phase === ph ? "tu-step-dot--active" : "",
                phase > ph ? "tu-step-dot--done" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {phase > ph ? "✓" : ph}
            </div>
            {ph < 3 && (
              <div
                className={`tu-step-line${phase > ph ? " tu-step-line--done" : ""}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <AmountForm
          amount={amount}
          onChange={setAmount}
          // onNext={goToStep2}
          onNext={goToStep3}
          onCancel={handleClose}
        />
      )}

      {step === 2 && (
        <AccountPicker
          network={network}
          onSelect={selectAccount}
          onBack={goBack}
        />
      )}

      {step === 3 && (selectedAccount || treasuryAccount) && (
        <SigningReview
          account={selectedAccount || treasuryAccount}
          cardNumber={cardNumber}
          cardAliasAddress={cardAliasAddress}
          amount={amount}
          network={network}
          onBack={goBack}
          onClose={handleClose}
          onSuccess={handleSuccess}
          setPhase={setPhase}
        />
      )}
    </CustomModal>
  );
}

export default TopUpModal;
